import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { Logger } from 'fsts';
import entrajobsSearchData, {
  EntraJobsSearchData,
  LogAction,
} from '@/shared/model/smallPayloadModels/entrajobsSearchData';
import DateField from '@/components/_common/date-field/DateField.vue';
import DateUtils from '@/shared/utils/DateUtils';

const logger = new Logger('search-form-entrajobs');
const authModule = namespace('auth');
const entraJobsModule = namespace('entraJobs');

type EntraFilterType = 'jobName' | 'action' | 'entity';

@Component({
  components: { DateField },
})
export default class SearchFormEntrajobs extends Vue {
  @entraJobsModule.Action('updateEntraJobsSearchData')
  private actionUpdateEntraJobsSearchData!: any;
  @entraJobsModule.Getter('isSearchDataEmpty')
  private isSearchDataEmpty!: any;
  @entraJobsModule.Getter('getEntraJobsSearchData')
  private getEntraJobsSearchData!: any;
  @entraJobsModule.Getter('isEntraJobsSearchDataEmpty')
  private isEntraJobsSearchDataEmpty!: any;
  @entraJobsModule.Getter('getEntraJobsSearchParams')
  private entraJobsSearchParams!: any;

  @entraJobsModule.Action('getJobsNameList')
  private actionGetJobsNameList!: any;
  @entraJobsModule.Action('getEntityList')
  private actionGetEntityList!: any;
  @entraJobsModule.Action('getListAction')
  private actionGetListAction!: any;

  @authModule.Getter('isUserSupportOrSuperAdmin')
  private isUserInAzureAdminGroupGetter!: any;
  @authModule.Getter('isUserOnlyGs')
  private isUserOnlyGs!: any;

  @authModule.Getter('zrNummer')
  private zrNummer!: any;
  @authModule.Getter('zrNummerWithRoles')
  private zrNummerWithRoles!: any;
  @authModule.Getter('isDevEnv')
  private isDevEnv!: any;

  listJobsName = [];
  listEntity = [];
  listAction = [];
  entraJobsLocalSearchData = entrajobsSearchData.defaultData();

  public isLoadingFilters: Record<EntraFilterType, boolean> = {
    jobName: false,
    action: false,
    entity: false,
  };

  private lastFetchSnapshots: Record<EntraFilterType, string | null> = {
    jobName: null,
    action: null,
    entity: null,
  };

  created() {
    this.entraJobsLocalSearchData = this.getEntraJobsSearchData || entrajobsSearchData.defaultData();
  }

  async mounted() {
    await Promise.all([this.getJobsNameList(), this.getEntityList(), this.getListAction()]);

    this.lastFetchSnapshots.jobName = this.getDependencySnapshot('jobName');
    this.lastFetchSnapshots.action = this.getDependencySnapshot('action');
    this.lastFetchSnapshots.entity = this.getDependencySnapshot('entity');
  }

  private normalizeDate(val: any): string | null {
    if (!val) return null;
    if (Array.isArray(val)) {
      const validDates = val.filter(Boolean).map(String).sort();
      return validDates.length > 0 ? validDates.join('~') : null;
    }
    return String(val);
  }

  private getDependencySnapshot(excludeFilterName: EntraFilterType): string {
    const state = {
      jobName: excludeFilterName === 'jobName' ? [] : [...(this.entraJobsLocalSearchData.jobName || [])].sort(),
      action: excludeFilterName === 'action' ? [] : [...(this.entraJobsLocalSearchData.action || [])].sort(),
      entity: excludeFilterName === 'entity' ? [] : [...(this.entraJobsLocalSearchData.entity || [])].sort(),
      timestamp: this.normalizeDate(this.entraJobsLocalSearchData.timestamp),
      facetChips: JSON.stringify(this.entraJobsSearchParams?.andClauseFieldsIds || []),
    };

    return JSON.stringify(state);
  }

  public async onFocusFilter(filterName: EntraFilterType) {
    const currentSnapshot = this.getDependencySnapshot(filterName);

    if (this.lastFetchSnapshots[filterName] === currentSnapshot) {
      return;
    }

    this.isLoadingFilters[filterName] = true;

    try {
      if (filterName === 'jobName') {
        await this.getJobsNameList();
      } else if (filterName === 'action') {
        await this.getListAction();
      } else if (filterName === 'entity') {
        await this.getEntityList();
      }

      this.lastFetchSnapshots[filterName] = currentSnapshot;
    } catch (error) {
      logger.error(`Error loading filter for ${filterName}:`, error);
    } finally {
      this.isLoadingFilters[filterName] = false;
    }
  }

  private async getJobsNameList() {
    console.log('getJobsNameList result:>> ');
    this.isLoadingFilters.jobName = true;
    try {
      const result: any = await this.actionGetJobsNameList({ entraJobsSearchData: this.getPayloadForFilterFetch() });
      this.listJobsName = result?.data || [];
    } catch (err: any) {
      logger.error(err);
    } finally {
      this.isLoadingFilters.jobName = false;
    }
  }

  private async getEntityList() {
    console.log('getEntityList result:>> ');
    this.isLoadingFilters.entity = true;
    try {
      const result: any = await this.actionGetEntityList({ entraJobsSearchData: this.getPayloadForFilterFetch() });
      this.listEntity = result?.data || [];
    } catch (err: any) {
      logger.error(err);
    } finally {
      this.isLoadingFilters.entity = false;
    }
  }

  private async getListAction() {
    console.log('getListAction result:>> ');
    this.isLoadingFilters.action = true;
    try {
      const result: any = await this.actionGetListAction({ entraJobsSearchData: this.getPayloadForFilterFetch() });
      this.listAction = result?.data || [];
    } catch (err: any) {
      logger.error(err);
    } finally {
      this.isLoadingFilters.action = false;
    }
  }

  private getPayloadForFilterFetch(): EntraJobsSearchData {
    return {
      ...this.entraJobsLocalSearchData,
      searchFacetFields: this.entraJobsSearchParams?.andClauseFieldsIds || [],
    };
  }

  isSearchDataWasCleared = false;
  @Watch('isEntraJobsSearchDataEmpty', { deep: true })
  public async onIsSearchDataEmpty(newVal: boolean) {
    logger.log('isSearchDataEmpty isSearchDataEmpty :>> ', newVal);
    if (newVal === true) {
      this.isSearchDataWasCleared = true; // avoid eternal loop on clearing `admpNewsSearchData` (watcher glitch without this variable)
      this.entraJobsLocalSearchData = entrajobsSearchData.defaultData();
    }
  }

  @Watch('entraJobsLocalSearchData', { deep: true })
  public async onOptionsFilterChanged(newVal: EntraJobsSearchData) {
    if (!this.isSearchDataWasCleared) {
      const payload: EntraJobsSearchData = {
        ...newVal,
      };

      this.actionUpdateEntraJobsSearchData(payload); // (GSP-075) use `Object.assign` to break reference for `admpNewsSearchData` to get new data from backend on any input clear or multiple select
    }
    this.isSearchDataWasCleared = false;
  }

  @Watch('entraJobsSearchParams.andClauseFieldsIds', { deep: true })
  public async onFacetChipsChanged() {
    this.lastFetchSnapshots = {
      jobName: null,
      action: null,
      entity: null,
    };

    await Promise.all([this.getJobsNameList(), this.getEntityList(), this.getListAction()]);
    this.lastFetchSnapshots.jobName = this.getDependencySnapshot('jobName');
    this.lastFetchSnapshots.action = this.getDependencySnapshot('action');
    this.lastFetchSnapshots.entity = this.getDependencySnapshot('entity');
  }

  changeDate(value: any) {
    this.entraJobsLocalSearchData.timestamp = value || null;
  }

  searchStatusInput = '';
  searchSupplierInput = '';

  clearSearchInput() {
    this.searchStatusInput = '';
    this.searchSupplierInput = '';
  }

  // get isSearchFormHasData() {
  //   const searchDataItem = this.admpNewsSearchData;
  //   return searchDataItem.status !== null || searchDataItem.favorite !== null || !!searchDataItem.datum;
  // }

  get jobNameTooltipList(): string[] {
    const selectedIds = this.entraJobsLocalSearchData.jobName || [];

    return this.listJobsName.filter((item: any) => selectedIds.includes(item.jobName)).map((item: any) => item.jobName);
  }

  get entityTooltipList(): string[] {
    const selectedIds = this.entraJobsLocalSearchData.entity || [];
    return this.listEntity
      .filter((item: any) => selectedIds.includes(item.entityName))
      .map((item: any) => item.displayName);
  }

  get actionTooltipList(): string[] {
    const selectedIds = this.entraJobsLocalSearchData.action || [];
    return this.listAction
      .filter((item: any) => selectedIds.includes(item.actionName))
      .map((item: any) => item.actionName);
  }
}
